<template>
  <v-container fluid pb-4>
    <v-row>
      <v-col>
        <v-card>
          <v-toolbar :dark="$vuetify.theme.dark" color="primary">
            <v-toolbar-title>
              {{ $t('Управление') }}
            </v-toolbar-title>
          </v-toolbar>
          <v-card-text>
            <v-data-iterator
                :items="blocks"
                hide-default-footer
                disable-pagination
                disable-filtering
                item-key="header"
                :locale="systemLocale">
              <template v-slot:header>
                <v-progress-linear :active="isLoading" color="loader" indeterminate />
              </template>
              <template v-slot="props">
                <v-row justify="center">
                  <v-col
                      v-for="block in props.items"
                      :key="block.header"
                      cols="12"
                      sm="12"
                      md="6"
                      lg="5"
                      xl="4">
                    <v-card>
                      <manage-block :item="block" />
                    </v-card>
                  </v-col>
                </v-row>
              </template>
            </v-data-iterator>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import ManageBlock from '@/components/reused/common/ManageBlock.vue'
  import {
    ORG_REQUEST_STATUSES,
    AUTHORIZATION_DOCUMENT_TYPES
  } from '@/constants/business'
  import { mapActions, mapState } from 'vuex'
  import { STATISTICS } from '@/constants/store.js'

  export default {
    components: {
      ManageBlock
    },
    data () {
      return {
        interval: null,
        isLoading: false
      }
    },
    computed: {
      ...mapState(STATISTICS.NAMESPACE, {
        businessStatistics: state => (state[STATISTICS.STATE.BUSINESS_STATISTICS] || { })
      }),
      blocks () {
        return [
          {
            header: this.$t('Торговые процедуры'),
            icon: 'gavel',
            links: [
              {
                id: 'summarizing',
                name: this.$t('Подведение итогов'),
                route: `${this.urlRootPath}/lots/summarizing`,
                count: this.businessStatistics.lotsWithoutTradeResults
              },
              {
                id: 'activeSessions',
                name: this.$t('Активные сессии'),
                route: `${this.urlRootPath}/sessions/active`,
                count: this.businessStatistics.activeSessions
              }
            ]
          },
          {
            header: this.$t('Организации'),
            icon: 'business',
            links: [
              {
                id: 'organizations',
                name: this.$t('Список организаций'),
                route: `${this.urlRootPath}/organizations`
              },
              {
                id: 'organizationsReport',
                name: this.$t('Выгрузка списка организаций'),
                route: `${this.urlRootPath}/reports/organizations`
              }
            ]
          },
          {
            header: this.$t('УП организаций'),
            icon: 'people',
            links: [
              {
                id: 'users',
                name: this.$t('Список УП'),
                route: `${this.urlRootPath}/users`
              },
              {
                id: AUTHORIZATION_DOCUMENT_TYPES.USER_AUTHORIZATION_DOCUMENT,
                name: this.$t('Документы, подтверждающие полномочия представителей Пользователя ЭПЭ'),
                route: `${this.urlRootPath}/authorizationDocuments/${AUTHORIZATION_DOCUMENT_TYPES.USER_AUTHORIZATION_DOCUMENT}`,
                count: this.businessStatistics.userAuthorizationDocument
              }
            ]
          },
          {
            header: this.$t('Регистрация организаций'),
            icon: 'person',
            links: [
              {
                id: 'organizationRegistration',
                name: this.$t('Запросы на регистрацию организаций'),
                route: `${this.urlRootPath}/organizationRegistrationRequests/${ORG_REQUEST_STATUSES.INPROCESS}`,
                count: this.businessStatistics.organizationRegistration
              },
              {
                id: 'userRegistration',
                name: this.$t('Запросы на регистрацию УП'),
                route: `${this.urlRootPath}/userRegistrationRequests`,
                count: this.businessStatistics.userRegistration
              }
            ]
          }, {
            header: this.$t('Прочее'),
            icon: 'settings_applications',
            links: [
              {
                id: 'globalMailing',
                name: this.$t('Общая Email рассылка'),
                route: `${this.urlRootPath}/globalMailing`
              },
              {
                id: 'participantsMailing',
                name: this.$t('Адресная Email рассылка'),
                route: `${this.urlRootPath}/participantsMailing`
              },
              {
                id: 'productionCalendar',
                name: this.$t('Производственный календарь'),
                route: `${this.urlRootPath}/productionCalendar`
              },
              {
                id: 'themes',
                name: this.$t('Настройки темы'),
                route: `${this.urlRootPath}/themes`
              },
              {
                id: 'staticPages',
                name: this.$t('Редактируемые страницы'),
                route: `${this.urlRootPath}/staticPages`
              },
              {
                id: 'staticFiles',
                name: this.$t('Файлы общего назначения'),
                route: `${this.urlRootPath}/staticFiles`
              },
              {
                id: 'smtpSettings',
                name: this.$t('Настройки SMTP'),
                route: `${this.urlRootPath}/smtpSettings`
              },
              {
                id: 'segmentSettings',
                name: this.$t('Настройки сегмента'),
                route: `${this.urlRootPath}/segmentSettings`
              }
            ]
          },
          {
            header: this.$t('Справочники'),
            icon: 'table_chart',
            links: [
              {
                id: 'incoterms',
                name: this.$t('Условия и базис поставки'),
                route: `${this.urlRootPath}/incoterms`
              }
            ]
          }
        ]
      }
    },
    beforeDestroy () {
      if (this.interval) {
        clearInterval(this.interval)
      }
    },
    created () {
      const vm = this
      vm.clearStore().then(() => {
        vm.getStatistics()
        // каждые 5 минут запрашиваем статистику
        if (!vm.interval) {
          vm.interval = setInterval(() => {
            vm.getStatistics()
          }, 300000)
        }
      })
    },
    methods: {
      ...mapActions(STATISTICS.NAMESPACE, {
        getBusinessStatisticsAction: STATISTICS.ACTIONS.GET_BUSINESS_STATISTICS,
        clearBusinessStatisticsAction: STATISTICS.ACTIONS.CLEAR_BUSINESS_STATISTICS
      }),
      clearStore: async function () {
        await Promise.all([
          this.clearBusinessStatisticsAction()
        ])
      },
      getStatistics: async function () {
        try {
          this.isLoading = true
          await Promise.all([
            this.getBusinessStatisticsAction()
          ])
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      }
    }
  }
</script>
